import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  FileText, Settings, CheckCircle, AlertCircle, Clock, Truck,
  Printer, Package, Scissors, BookOpen, Layers
} from 'lucide-react'
import { dashboardService } from '../services/dashboardService'

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalSPK: 0,
    inProgress: 0,
    completed: 0,
    urgent: 0,
    validation: 0,
    atVendor: 0
  })
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    try {
      const response = await dashboardService.getStats()
      if (response.success) {
        setStats(response.data.stats || stats)
        setVendors(response.data.vendors || [])
      }
    } catch (error) {
      console.error('Error loading dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const statCards = [
    { label: 'Total SPK', value: stats.totalSPK, icon: FileText, color: 'bg-blue-100 text-blue-600' },
    { label: 'Proses', value: stats.inProgress, icon: Settings, color: 'bg-purple-100 text-purple-600' },
    { label: 'Selesai', value: stats.completed, icon: CheckCircle, color: 'bg-green-100 text-green-600' },
    { label: 'Urgent', value: stats.urgent, icon: AlertCircle, color: 'bg-red-100 text-red-600' },
    { label: 'Validasi', value: stats.validation, icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
    { label: 'Di Vendor', value: stats.atVendor, icon: Truck, color: 'bg-pink-100 text-pink-600' },
  ]

  const productionFlows = [
    {
      title: 'ISI (Offset)',
      icon: '📄',
      color: 'border-l-blue-500 bg-blue-50',
      flow: 'Validasi → Cetak → Potong → QC Cetak* → Susun → QC Halaman* → Main'
    },
    {
      title: 'ISI (Comcolor)',
      icon: '🖨️',
      color: 'border-l-cyan-500 bg-cyan-50',
      flow: 'Validasi → Cetak → Potong → QC Halaman* → Main',
      note: '(skip Susun)'
    },
    {
      title: 'COVER',
      icon: '📕',
      color: 'border-l-red-500 bg-red-50',
      flow: 'Cetak → QC Cover → Laminasi → Main'
    },
    {
      title: 'SISIPAN',
      icon: '📑',
      color: 'border-l-green-500 bg-green-50',
      flow: 'Cetak → QC Sisipan → Done',
      note: '(per sekolah custom)'
    },
    {
      title: 'MAIN',
      icon: '📚',
      color: 'border-l-purple-500 bg-purple-50',
      flow: 'Binding → QC Binding* → Potong Sisir → QC Potong → Gudang'
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3">
            <div className={`p-3 rounded-xl ${stat.color}`}>
              <stat.icon className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production Flow */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            Alur Produksi 3 Jalur
          </h2>
          <div className="space-y-3">
            {productionFlows.map((flow, index) => (
              <div 
                key={index} 
                className={`p-4 rounded-lg border-l-4 ${flow.color}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{flow.icon}</span>
                  <span className="font-semibold text-gray-800">{flow.title}:</span>
                </div>
                <div className="text-sm text-gray-600 ml-7">
                  {flow.flow.split('→').map((step, i, arr) => (
                    <span key={i}>
                      <span className={step.includes('*') ? 'text-blue-600 font-medium' : ''}>
                        {step.trim()}
                      </span>
                      {i < arr.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                    </span>
                  ))}
                </div>
                {flow.note && (
                  <div className="text-xs text-gray-500 ml-7 mt-1 italic">{flow.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Vendor Status */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-pink-600" />
            Status Vendor
          </h2>
          <div className="space-y-3">
            {vendors.length > 0 ? vendors.map((vendor, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-800">{vendor.name}</div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  vendor.activeJobs > 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {vendor.activeJobs} job aktif
                </span>
              </div>
            )) : (
              <>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800">CV. Mitra Cetak Jaya</div>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    1 job aktif
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800">PT. Grafika Nusantara</div>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    2 job aktif
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800">UD. Sinar Print</div>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-600">
                    0 job aktif
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Aksi Cepat</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/spk/create" className="flex flex-col items-center gap-2 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
            <FileText className="w-8 h-8 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">SPK Baru</span>
          </Link>
          <Link to="/queue" className="flex flex-col items-center gap-2 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
            <Layers className="w-8 h-8 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Antrian</span>
          </Link>
          <Link to="/station-update" className="flex flex-col items-center gap-2 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
            <Settings className="w-8 h-8 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Update Stasiun</span>
          </Link>
          <Link to="/warehouse" className="flex flex-col items-center gap-2 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
            <Package className="w-8 h-8 text-orange-600" />
            <span className="text-sm font-medium text-gray-700">Gudang</span>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
